'use client';

import React from 'react';
import { usePathname } from 'next/navigation';
import { IResortPageInfo, IResortPathInfo, IResortShortWeatherInfo } from '@/api/resort/types';
import { BottomMountains } from '@/components/blocks/BottomMountains';
import { Footer } from '@/components/blocks/Footer';
import { Container } from '@/components/ui/container';
import { HeaderNav, HeaderNavCurLink } from '@/components/common/HeaderNav';
import { Layout } from '@/components/resort/Layout/Layout';
import { CurrentLink, CurrentTab } from '@/components/resort/ResortHeaderNav/types';
import { ResortHeaderNav } from '@/components/resort/ResortHeaderNav';
import { AdUnitContext } from '@/tracking/ads/components/AdUnitContext';
import { TopAd } from '@/tracking/ads/components/TopAd';
import { I18nKey } from '@/i18n/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { defaultImage } from '@/page-components/resort/constants';
import { ResortFPCTracker } from '@/tracking/facebook/components/ResortFPCTracker';
import { IRelatedForResort } from '@/api/region/types';
import { TemplateProps } from '@/page-components/pathTypes';
import { isEmpty } from '@/util/isEmpty';
import { ContactInformation } from '@/components/resort/ContactInformation';
import { rsUrl } from '@/routes';
import WeskiWisepopsPopupDiv from '@/components/common/WeskiWisepopsPopupDiv';

const RESORT_TEMPLATE_NAMES_WITH_BOTTOM_WESKI_POPUP = new Set([
  'Resort Overview Resort',
  'Resort Reviews',
  'Resort Historical Snowfall',
  'Resort Lift Tickets',
]);

export interface IResortLayout extends TemplateProps {
  pathInfo: IResortPathInfo,
  resort: IResortPageInfo,
  shortWeather: IResortShortWeatherInfo,
  related: IRelatedForResort,
  adTKey: string,
  meta: {
    title: I18nKey['seo'] | I18nKey['common'],
    description: I18nKey['seo'] | I18nKey['common'],
    redirectTo: string | null,
    dynamic: { 'snow': { 'last24': number } },
  },
  navigation: {
    head: HeaderNavCurLink,
    tab: CurrentTab,
    link: CurrentLink,
  },
  hero: {
    title: I18nKey['common'],
    showVideo: boolean,
  },
  templateName: string;
}

const snowMeta = (report: IResortShortWeatherInfo) => {
  const { snow, temp, symbol } = (report || {});
  let Fs: string | undefined;
  let Rs: string | undefined;
  let Th: string | undefined;
  let Tl: string | undefined;

  // Today’s Weather Condition
  const Wc: string | undefined = symbol;

  if (snow) {
    // Forecasted Snow 72 hours
    // 0, 1-7, 8-15, 16-23, 24+
    // Projected snowfall over next 72 hours in cm’s.
    // If the amount falls in a range then we insert the range in the ad call.
    // e.g. If Forecasted snow is = 1,2,3,4,5,6,7 cm’s then the range of 1-7 would be inserted. If > = 24 cm then 24+ would be inserted.
    if (!isEmpty(snow.forecast72)) {
      Fs = '0';
      if (snow.forecast72 >= 0.5 && snow.forecast72 < 7.5) {
        Fs = '1-7';
      }
      if (snow.forecast72 >= 7.5 && snow.forecast72 < 15.5) {
        Fs = '8-15';
      }
      if (snow.forecast72 >= 15.5 && snow.forecast72 < 23.5) {
        Fs = '16-23';
      }
      if (snow.forecast72 >= 23.5) {
        Fs = '24plus';
      }
    }

    // Snow Reported 72 hours.
    // Resort reported snowfall over last 72 hours in cm’s.
    if (!isEmpty(snow.last72)) {
      Rs = '0';
      if (snow.last72 >= 0.5 && snow.last72 < 7.5) {
        Rs = '1-7';
      }
      if (snow.last72 >= 7.5 && snow.last72 < 15.5) {
        Rs = '8-15';
      }
      if (snow.last72 >= 15.5 && snow.last72 < 23.5) {
        Rs = '16-23';
      }
      if (snow.last72 >= 23.5) {
        Rs = '24plus';
      }
    }

    // Current Base Temp (Low)
    // Today’s Forecasted Low Temperature in Celsius.
    if (!isEmpty(temp.min)) {
      Tl = '--15';
      if (temp.min >= -14.5 && temp.min < -6.5) {
        Tl = '-14-7';
      }
      if (temp.min >= -6.5 && temp.min < 0.5) {
        Tl = '-6-0';
      }
      if (temp.min >= 0.5 && temp.min < 11.5) {
        Tl = '1-11';
      }
      if (temp.min >= 11.5) {
        Tl = '12plus';
      }
    }

    // Current Base Temp (High)
    // Today’s Forecasted High Temperature in Celsius.
    if (!isEmpty(temp.max)) {
      Th = '--15';
      if (temp.max >= -14.5 && temp.max < -6.5) {
        Th = '-14-7';
      }
      if (temp.max >= -6.5 && temp.max < 0.5) {
        Th = '-6-0';
      }
      if (temp.max >= 0.5 && temp.max < 11.5) {
        Th = '1-11';
      }
      if (temp.max >= 11.5) {
        Th = '12plus';
      }
    }
  }

  return ({
    // Forecasted Snow 72 hours
    Fs,
    Rs,
    Th,
    Tl,
    Wc,
  });
};

// noOffersContext used to gate the PlanYourTrip widget when the
// TripAdvisor offers feed came back empty. TripAdvisor accommodation
// flow has been removed (the /lodging route is gone too), so the
// context is now hard-wired to `false` for source-compatibility with
// any consumer that still references it. Safe to drop entirely in a
// follow-up.
export const noOffersContext = React.createContext(false);

export const ResortLayout = ({
  children,
  pathInfo,
  resort,
  adTKey,
  meta,
  navigation,
  shortWeather,
  hero,
  templateName,
  related,
}: IResortLayout & { children: React.ReactNode }) => {
  const { t } = useOTSTranslation('common');
  const pathname = usePathname();
  const shouldRenderBottomWeskiPopup = RESORT_TEMPLATE_NAMES_WITH_BOTTOM_WESKI_POPUP.has(templateName);
  const isOnSummerOverview = pathname?.endsWith(rsUrl('summer-overview')) ?? false;
  const video = isOnSummerOverview ? resort.videoSummer : resort.video;

  const resortTitle = pathInfo.title_short ?? resort.title;

  return (
    <AdUnitContext
      region={resort.region}
      resort={pathInfo}
      adType="resort"
      adTKey={adTKey}
      meta={{
        snow: ((meta.dynamic?.snow?.last24 ?? 0) >= 7) ? 'yes' : 'no',
        ...snowMeta(shortWeather),
      }}
    >
      <ResortFPCTracker
        uuid={resort.uuid}
        region={pathInfo.region}
        shortTitle={pathInfo.title_short}
      />
      <HeaderNav current={navigation.head} isOnSummerOverview={isOnSummerOverview} />
      <TopAd id={`resort-${pathInfo.uuid}`} templateName={templateName} />
      <Container width="xl-only" className="content-container">
        <ResortHeaderNav
          resort={pathInfo}
          tab={navigation.tab}
          link={navigation.link}
          liftsUrl={resort.liftsUrl}
          photo={resort?.image ?? defaultImage}
          title={t(hero.title, { resort: resort.title })}
          website={resort.website}
          phone={resort.phone}
          isPartner={resort.isPartner}
          video={hero.showVideo ? video : undefined}
          region={resort.region}
          related={related}
          isOnSummerOverview={isOnSummerOverview}
        />
        {/* Skip the legacy .layout-container class (globals.scss):
            its mobile rule pulls content up by -1rem to overlap the
            old photo HeroNav, which now clips the v3 SubNav. The lg
            white background + 1rem vertical padding it added are
            re-applied here in Tailwind. */}
        <Layout className="bg-white lg:py-4">
          <noOffersContext.Provider value={false}>{children}</noOffersContext.Provider>
        </Layout>

      </Container>
      <ContactInformation
        title={resortTitle}
        uuid={resort.uuid}
        isOnSummerOverview={isOnSummerOverview}
      />
      <BottomMountains isOnSummerOverview={isOnSummerOverview} />
      <Footer isOnSummerOverview={isOnSummerOverview} />
      {shouldRenderBottomWeskiPopup && (
        <WeskiWisepopsPopupDiv isRegion={false} id={resort.uuid} />
      )}
    </AdUnitContext>
  );
};
